import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import PaymentStatus from './PaymentStatus';
import { paymentService, validatePhoneForProvider } from './paymentService';

const Spinner = () => (
  <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-transparent" />
);

const ProviderLogo = ({ src, fallbackSrc, alt }) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [failed, setFailed] = useState(false);
  if (failed) return null;

  return (
    <img
      src={currentSrc}
      alt={alt}
      loading="lazy"
      onError={() => {
        if (fallbackSrc && currentSrc !== fallbackSrc) {
          setCurrentSrc(fallbackSrc);
          return;
        }
        setFailed(true);
      }}
      className="h-8 w-8 rounded-md object-contain bg-white"
    />
  );
};

export default function PaymentPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const listingId = params.get('listingId') || '';
  const purchase = params.get('purchase') || '';
  const scenario = params.get('scenario') || '';

  const isCreditPurchase = purchase === 'credit' || !listingId;

  const [provider, setProvider] = useState('MOOV_MONEY');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | pending | success | failure
  const [statusMessage, setStatusMessage] = useState('');
  const [paymentId, setPaymentId] = useState('');

  const amountLabel = useMemo(
    () => `${new Intl.NumberFormat('fr-FR').format(paymentService.amountFcfa)} FCFA`,
    []
  );

  const phoneCheck = useMemo(() => validatePhoneForProvider(provider, phone), [provider, phone]);

  const scope = useMemo(() => (isCreditPurchase ? `credit_${provider}` : `listing_${listingId}`), [isCreditPurchase, provider, listingId]);

  const canPay = phoneCheck.valid && !submitting && status !== 'pending' && !paymentService.isPaymentPending(scope);

  const payNow = async () => {
    setSubmitting(true);
    setStatus('pending');
    setStatusMessage('Traitement du paiement…');

    try {
      const intent = await paymentService.createPaymentIntent({
        listingId: isCreditPurchase ? '' : listingId,
        provider,
        phone,
        amountFcfa: paymentService.amountFcfa,
        scenario: scenario || undefined,
      });

      setPaymentId(intent.paymentId || intent.id || '');

      // Poll once (mock resolves immediately). Real integration should poll until terminal state.
      const res = await paymentService.getPaymentStatus({
        paymentId: intent.paymentId || intent.id,
        listingId: isCreditPurchase ? '' : listingId,
        provider,
        scenario: intent.scenario || scenario || undefined,
      });

      if (res.status === 'success') {
        if (isCreditPurchase) {
          paymentService.addCredits(1);
        } else {
          paymentService.markListingAsPaid(listingId);
        }
        setStatus('success');
        setStatusMessage(isCreditPurchase ? 'Paiement validé. Crédit ajouté.' : 'Paiement validé. Vous pouvez maintenant publier.');
      } else if (res.status === 'failure') {
        setStatus('failure');
        setStatusMessage('Paiement échoué. Réessayez.');
      } else {
        setStatus('pending');
        setStatusMessage('Paiement en attente de confirmation…');
      }
    } catch (e) {
      setStatus('failure');
      setStatusMessage(e?.message || 'Erreur paiement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-white">
      <div className="relative overflow-hidden">
        <div className="absolute inset-x-0 -top-32 h-80 bg-gradient-to-br from-orange-500 to-orange-600" />
        <div className="absolute inset-x-0 -top-24 h-72 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_45%)]" />

        <div className="container mx-auto px-4 pt-6 pb-10 relative">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-orange-100">IMMO&CO</p>
              <h1 className="text-lg sm:text-xl font-semibold text-white">Paiement Mobile Money</h1>
              <p className="text-xs text-orange-100 mt-1">10 000 FCFA par publication</p>
            </div>
            <Button type="button" variant="outline" onClick={() => navigate('/')} className="bg-white/10 text-white border-white/20 hover:bg-white/20">
              Retour
            </Button>
          </div>

          <div className="mt-6 max-w-xl mx-auto">
            <div className="rounded-2xl bg-white shadow-lg border border-gray-100 overflow-hidden">
              <div className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500">Résumé</p>
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 mt-1">{isCreditPurchase ? 'Achat crédit de publication' : 'Paiement publication'}</h2>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 px-4 py-3 text-white shadow-sm">
                    <p className="text-[10px] uppercase tracking-wide text-orange-100">Montant</p>
                    <p className="text-lg font-bold leading-tight">{amountLabel}</p>
                    <p className="text-[11px] text-orange-100 mt-1">Mobile Money (FCFA)</p>
                  </div>
                </div>

                {!listingId && !isCreditPurchase ? (
                  <div className="mt-4 text-sm text-red-600">Listing introuvable (paramètre manquant).</div>
                ) : null}

                <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-500">Référence</p>
                    <p className="text-xs font-mono text-gray-700 break-all text-right">{isCreditPurchase ? 'Crédit de publication' : listingId}</p>
                  </div>
                </div>

                <div className="mt-6">
                  <p className="text-sm font-semibold text-gray-900">Choisir un opérateur</p>
                  <p className="text-xs text-gray-500 mt-1">Sélectionne Moov Money ou Airtel Money pour payer.</p>

                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setProvider('MOOV_MONEY')}
                      className={`rounded-2xl border p-4 text-left transition ${provider === 'MOOV_MONEY' ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-3">
                        <ProviderLogo src="/payments/moov-money.png" fallbackSrc="/payments/moov-money.svg" alt="Moov Money" />
                        <div>
                          <div className="text-sm font-semibold text-gray-900">Moov</div>
                          <div className="text-xs text-gray-500">Moov Money</div>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setProvider('AIRTEL_MONEY')}
                      className={`rounded-2xl border p-4 text-left transition ${provider === 'AIRTEL_MONEY' ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-3">
                        <ProviderLogo src="/payments/airtel-money.png" fallbackSrc="/payments/airtel-money.svg" alt="Airtel Money" />
                        <div>
                          <div className="text-sm font-semibold text-gray-900">Airtel</div>
                          <div className="text-xs text-gray-500">Airtel Money</div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="text-sm font-semibold text-gray-900">Numéro Mobile Money</label>
                  <p className="text-xs text-gray-500 mt-1">Saisis le numéro associé à ton portefeuille Mobile Money.</p>
                  <div className="mt-3">
                    <Input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="061234567 ou +241061234567"
                      className="h-11 rounded-xl focus-visible:ring-orange-500"
                    />
                  </div>
                  {!phoneCheck.valid && phone ? (
                    <p className="text-xs text-red-600 mt-2">{phoneCheck.error}</p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-2">Format accepté : 9 chiffres ou +241 + 9 chiffres.</p>
                  )}
                </div>

                {status !== 'idle' ? (
                  <div className="mt-6 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <PaymentStatus status={status === 'pending' ? 'pending' : status} message={statusMessage} />
                    {paymentId ? (
                      <p className="mt-2 text-xs text-gray-500 break-all">ID: {paymentId}</p>
                    ) : null}
                  </div>
                ) : null}

                {status === 'success' ? (
                  <div className="mt-4 rounded-xl bg-green-50 border border-green-200 p-4 text-sm text-green-800">
                    Confirmation : paiement validé.
                  </div>
                ) : null}
              </div>

              <div className="border-t border-gray-100 bg-white p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    onClick={payNow}
                    disabled={!canPay}
                    className="w-full bg-orange-500 hover:bg-orange-600"
                  >
                    {submitting || status === 'pending' ? (
                      <span className="inline-flex items-center gap-2"><Spinner /> Payer maintenant</span>
                    ) : (
                      'Payer maintenant'
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="w-full"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
